import {useEffect, useMemo, useState} from 'react';
import useUnifiedPatients from '../common/useUnifiedPatients';
import {searchNewEncounterEhrPatientsByName} from '../../services/newEncounterPatientSearchService';
import {patientDisplayName} from './createSessionFormUtils';
import {newEncounterPatientMatchesNamePrefix} from './newEncounterPatientSearch';
import {
  isMockEhrEnabled,
  MOCK_EHR_PATIENTS,
  MOCK_UNIFIED_PATIENTS,
} from '../../mocks/mockEhrFixtures';

const INITIAL_PATIENT_RESULT_LIMIT = 20;
const SEARCH_PATIENT_RESULT_LIMIT = 25;

function patientItemEhrRankKeys(item: any) {
  return [
    item?.ehrPatient?.ehrSourceId,
    item?.ehrPatient?.ehr_source_id,
    item?.ehrPatient?.id,
    item?.ehrPatient?.cacheDocumentId,
    item?.localPatient?._ehrPatientId,
    item?.localPatient?.fhir_patient_id,
    item?.localPatient?.ehr_source_id,
    item?.ehrPatient?.mrn,
    item?.ehrPatient?.accountNo,
  ]
    .filter(Boolean)
    .map(value => String(value));
}

function patientItemIdentityKey(item: any) {
  const displayName = patientDisplayName(item).toLowerCase();
  return (
    [
      item?.localPatient?.id && `local:${item.localPatient.id}`,
      item?.ehrPatient?.ehrSourceId && `ehr:${item.ehrPatient.ehrSourceId}`,
      item?.ehrPatient?.ehr_source_id && `ehr:${item.ehrPatient.ehr_source_id}`,
      item?.ehrPatient?.id && `ehr:${item.ehrPatient.id}`,
      item?.patient?.localId && `local:${item.patient.localId}`,
      item?.patient?.ehrId && `ehr:${item.patient.ehrId}`,
      item?.patient?.id && `patient:${item.patient.id}`,
      displayName && `name:${displayName}`,
    ].filter(Boolean)[0] || ''
  );
}

function ehrPatientItem(patient: any, index: any) {
  const ehrId =
    patient?.ehrSourceId || patient?.ehr_source_id || patient?.id || index;
  return {
    id: `ehr_${ehrId}`,
    localPatient: null,
    ehrPatient: patient,
    matchKind: 'ehrOnly',
  };
}

function appendItemsWithoutEhrDuplicates(
  primaryItems: any[] = [],
  fallbackItems: any[] = [],
) {
  const seenEhrKeys = new Set(primaryItems.flatMap(patientItemEhrRankKeys));
  return [
    ...primaryItems,
    ...fallbackItems.filter(item => {
      const keys = patientItemEhrRankKeys(item);
      return !keys.length || !keys.some(key => seenEhrKeys.has(key));
    }),
  ];
}

function toScheduledPatientDate(value: any) {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isFinite(value.getTime()) ? value : null;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime())
      ? date
      : null;
  }
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function scheduledPatientSortDate(item: any) {
  return toScheduledPatientDate(
    item?.scheduledSortDate ||
      item?.scheduledAt ||
      item?.appointmentSortDate ||
      item?.sortDate,
  );
}

function nextScheduledPatientItems(items: any[] = [], now = new Date()) {
  const nowTime = now.getTime();
  const entries = items.map((item, index) => ({
    item,
    index,
    sortDate: scheduledPatientSortDate(item),
  }));
  const futureEntries = entries
    .filter(({sortDate}) => sortDate && sortDate.getTime() >= nowTime)
    .sort(
      (left, right) =>
        left.sortDate!.getTime() - right.sortDate!.getTime() ||
        left.index - right.index,
    );
  const futureEntryIndexes = new Set(futureEntries.map(({index}) => index));
  const remainingEntries = entries
    .filter(({index}) => !futureEntryIndexes.has(index))
    .sort((left, right) => left.index - right.index);
  const orderedItems = [...futureEntries, ...remainingEntries].map(
    ({item}) => item,
  );
  const seenPatients = new Set();
  const result = [];
  for (const item of orderedItems) {
    const key = patientItemIdentityKey(item);
    if (key && seenPatients.has(key)) continue;
    if (key) seenPatients.add(key);
    result.push(item);
  }
  return result;
}

export default function useCreateSessionPatients({
  userData,
  patientSearch,
  initialPatientItems = [],
}: Record<string, any>) {
  const {
    patients,
    hasActiveEhr,
    isLoading: isLoadingPatients,
  } = useUnifiedPatients({userData, clearWhenDisabled: true});
  const companyId = userData?.companyId || null;
  const isMockEhrMode = isMockEhrEnabled(userData);
  const [ehrPatients, setEhrPatients] = useState<any[]>([]);
  const [isSearchingEhrPatients, setIsSearchingEhrPatients] = useState(false);

  const patientItems = useMemo(() => {
    if (isMockEhrMode) return [...MOCK_UNIFIED_PATIENTS];
    return patients.map((patient: any) => ({
      id: `local_${patient.id}`,
      localPatient: patient,
      ehrPatient: null,
      matchKind: 'localOnly',
    }));
  }, [isMockEhrMode, patients]);
  const ehrSearchPatientItems = useMemo(
    () => (hasActiveEhr ? ehrPatients.map(ehrPatientItem) : []),
    [ehrPatients, hasActiveEhr],
  );
  const initialDropdownPatients = useMemo(
    () => nextScheduledPatientItems(initialPatientItems),
    [initialPatientItems],
  );

  useEffect(() => {
    const query = patientSearch.trim();
    if (isMockEhrMode) {
      const needle = query.toLowerCase();
      setEhrPatients(
        query
          ? MOCK_EHR_PATIENTS.filter(patient =>
              patient.displayName.toLowerCase().includes(needle),
            )
          : [],
      );
      setIsSearchingEhrPatients(false);
      return undefined;
    }
    if (!query || !hasActiveEhr || !companyId) {
      setEhrPatients([]);
      setIsSearchingEhrPatients(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearchingEhrPatients(true);

    searchNewEncounterEhrPatientsByName(companyId, query, {
      resultLimit: SEARCH_PATIENT_RESULT_LIMIT,
    })
      .then(patientsResult => {
        if (cancelled) return;
        setEhrPatients(patientsResult);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to search new encounter EHR patients:', error);
        setEhrPatients([]);
      })
      .finally(() => {
        if (!cancelled) setIsSearchingEhrPatients(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, hasActiveEhr, isMockEhrMode, patientSearch]);

  const filteredPatients = useMemo(() => {
    const query = patientSearch.trim();
    if (!query) {
      return initialDropdownPatients.slice(0, INITIAL_PATIENT_RESULT_LIMIT);
    }
    const matchingLocalItems = patientItems.filter((item: any) =>
      newEncounterPatientMatchesNamePrefix(query, item),
    );
    if (!hasActiveEhr) {
      return matchingLocalItems.slice(0, SEARCH_PATIENT_RESULT_LIMIT);
    }

    const matchingEhrItems = ehrSearchPatientItems.filter((item: any) =>
      newEncounterPatientMatchesNamePrefix(query, item),
    );
    return appendItemsWithoutEhrDuplicates(
      matchingEhrItems,
      matchingLocalItems,
    ).slice(0, SEARCH_PATIENT_RESULT_LIMIT);
  }, [
    ehrSearchPatientItems,
    hasActiveEhr,
    initialDropdownPatients,
    patientSearch,
    patientItems,
  ]);

  return {
    patientItems,
    filteredPatients,
    hasActiveEhr,
    isLoadingPatients,
    isSearchingEhrPatients,
  };
}
